/**
 * V4 calls for the Estimate Generator (gateway → rest_server). Business rules, numbers and permissions
 * stay on the server; this module only shapes requests and unwraps `{ estimate }` envelopes.
 */
import { api } from '@/lib/api';
import type {
  CreatedQuote, DeckWarning, DistanceResult, Estimate, EstimateDetail, EstimateList, EstimateListFilters, Lang,
  LifecycleAction, ProposalTemplate, QuoteDraftPayload, SaveEstimateBody, TemplateValidation,
} from './types';

const BASE = '/v4/estimates';
/** The gateway allows generate-deck 120 s (Google Drive + Slides); keep the browser waiting a bit longer. */
export const DECK_TIMEOUT_MS = 130000;
/** Template save/validate read the file from Google; the gateway allows 60 s. */
export const TEMPLATE_TIMEOUT_MS = 70000;

type One = { estimate: Estimate };

export function listEstimates(filters: EstimateListFilters = {}): Promise<EstimateList> {
  return api.get<EstimateList>(BASE, {
    opportunityId: filters.opportunityId,
    status: filters.status?.length ? filters.status.join(',') : undefined,
    ownerUserId: filters.ownerUserId,
    awaitingApproval: filters.awaitingApproval ? 'true' : undefined,
    pageNumber: filters.pageNumber ?? 0,
    pageLength: filters.pageLength ?? 25,
  });
}

export const getEstimate = (id: string) => api.get<EstimateDetail>(`${BASE}/${id}`);

export const createEstimate = (opportunityId: string) => api.post<One>(BASE, { opportunityId }).then((r) => r.estimate);

export const saveEstimate = (id: string, body: SaveEstimateBody) => api.put<One>(`${BASE}/${id}`, body).then((r) => r.estimate);

export const deleteEstimate = (id: string) => api.delete<void>(`${BASE}/${id}`);

export const runLifecycleAction = (id: string, action: LifecycleAction, body?: { comment: string }) =>
  api.post<One>(`${BASE}/${id}/${action}`, body).then((r) => r.estimate);

export const repriceEstimate = (id: string) => api.post<One>(`${BASE}/${id}/reprice`).then((r) => r.estimate);

export const reviseEstimate = (id: string) => api.post<One>(`${BASE}/${id}/revise`).then((r) => r.estimate);

export const generateDeck = (id: string) =>
  api.post<{ estimate: Estimate; warnings: DeckWarning[] }>(`${BASE}/${id}/generate-deck`, undefined, { timeoutMs: DECK_TIMEOUT_MS });

export const getQuoteDraft = (id: string, budgetKey: string, accountId?: string) =>
  api.get<{ payload: QuoteDraftPayload }>(`${BASE}/${id}/quote-draft`, { budgetKey, accountId }).then((r) => r.payload);

export const createQuote = (payload: QuoteDraftPayload) => api.post<{ quote: CreatedQuote }>('/v4/quotes', payload).then((r) => r.quote);

export const linkQuote = (id: string, quoteId: string, budgetKey: string) =>
  api.post<One>(`${BASE}/${id}/link-quote`, { quoteId, budgetKey }).then((r) => r.estimate);

export const lookupDistance = (address: string) => api.get<DistanceResult>('/v4/estimate-distance', { address });

export const listProposalTemplates = () => api.get<{ templates: ProposalTemplate[] }>('/v4/proposal-templates').then((r) => r.templates);

export function saveProposalTemplate(language: Lang, driveFileId: string, name?: string) {
  const body = name?.trim() ? { driveFileId, name: name.trim() } : { driveFileId };
  return api.put<{ template: ProposalTemplate; validation: TemplateValidation }>(
    `/v4/proposal-templates/monitoring/${language}`, body, { timeoutMs: TEMPLATE_TIMEOUT_MS },
  );
}

export const validateProposalTemplate = (id: string) =>
  api.post<{ template: ProposalTemplate; validation: TemplateValidation }>(`/v4/proposal-templates/${id}/validate`, undefined, { timeoutMs: TEMPLATE_TIMEOUT_MS });
